import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { CheckCircle, Clock, AlertCircle, Calendar, Target, Zap } from 'lucide-react';

interface ImplementationReadinessProps {
  data: Partial<CommunityAssessmentData>;
  onUpdate: (data: Partial<CommunityAssessmentData>) => void;
  tier: 'silver' | 'gold' | 'custom';
}

export const Step_6_ImplementationReadiness: React.FC<ImplementationReadinessProps> = ({
  data,
  onUpdate,
  tier,
}) => {
  const updateImplementationReadiness = (
    field: string,
    value: string | number | boolean | string[],
  ) => {
    onUpdate({
      ...data,
      implementationReadiness: {
        ...data.implementationReadiness,
        [field]: value,
      },
    });
  };

  const updateChangeReadiness = (method: string, checked: boolean) => {
    const currentMethods = data.implementationReadiness?.changeManagementNeeds || [];
    const updated = checked
      ? [...currentMethods, method]
      : currentMethods.filter((m) => m !== method);
    updateImplementationReadiness('changeManagementNeeds', updated);
  };

  const updateTrainingNeeds = (need: string, checked: boolean) => {
    const currentNeeds = data.implementationReadiness?.trainingNeeds || [];
    const updated = checked ? [...currentNeeds, need] : currentNeeds.filter((n) => n !== need);
    updateImplementationReadiness('trainingNeeds', updated);
  };

  const changeManagementOptions = [
    'Board training on new processes',
    'Resident communication campaign',
    'Gradual rollout to minimize disruption',
    'Champion identification & training',
    'Regular feedback collection',
    'Change resistance management',
    'Documentation of new procedures',
    'Integration with existing workflows',
  ];

  const trainingNeedOptions = [
    'Basic software navigation',
    'Financial reporting & analytics',
    'Communication & notifications',
    'Voting & decision management',
    'Document management',
    'Resident portal management',
    'Mobile app usage',
    'Data export & backup procedures',
    'Security & privacy best practices',
    'Advanced features & customization',
  ];

  const getTimelineBadge = (timeline: string) => {
    switch (timeline) {
      case 'immediate':
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Urgent</span>
        );
      case 'within_month':
        return (
          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Soon</span>
        );
      case 'within_quarter':
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
            This Quarter
          </span>
        );
      case 'within_year':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
            This Year
          </span>
        );
      case 'flexible':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Flexible</span>
        );
      default:
        return null;
    }
  };

  const getReadinessScore = () => {
    let score = 0;
    const readiness = data.implementationReadiness;

    if (readiness?.timeline === 'immediate' || readiness?.timeline === 'within_month') score += 2;
    else if (readiness?.timeline === 'within_quarter') score += 1;

    if (
      readiness?.decisionMakingTimeline === 'immediate' ||
      readiness?.decisionMakingTimeline === 'within_month'
    )
      score += 2;
    else if (readiness?.decisionMakingTimeline === 'within_quarter') score += 1;

    if (readiness?.stakeholderBuyIn === 'strong' || readiness?.stakeholderBuyIn === 'moderate')
      score += 2;
    else if (readiness?.stakeholderBuyIn === 'mixed') score += 1;

    if (
      readiness?.resourceAvailability === 'fully_available' ||
      readiness?.resourceAvailability === 'mostly_available'
    )
      score += 2;
    else if (readiness?.resourceAvailability === 'somewhat_available') score += 1;

    return Math.min(score, 8);
  };

  const readinessScore = getReadinessScore();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Implementation Readiness</h3>
        <p className="text-gray-600">
          Let's assess your community's readiness for change and plan the implementation
        </p>
      </div>

      <div className="space-y-6">
        {/* Timeline & Urgency */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              Implementation Timeline
            </CardTitle>
            <CardDescription>When do you hope to implement a new system?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="timeline">Desired Implementation Timeline</Label>
                <Select
                  value={data.implementationReadiness?.timeline || ''}
                  onValueChange={(value) => updateImplementationReadiness('timeline', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate (within 2 weeks)</SelectItem>
                    <SelectItem value="within_month">Within 1 month</SelectItem>
                    <SelectItem value="within_quarter">Within 3 months</SelectItem>
                    <SelectItem value="within_year">Within 1 year</SelectItem>
                    <SelectItem value="flexible">Timeline is flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="decisionTimeline">Decision Making Timeline</Label>
                <Select
                  value={data.implementationReadiness?.decisionMakingTimeline || ''}
                  onValueChange={(value) =>
                    updateImplementationReadiness('decisionMakingTimeline', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="When can you decide?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Can decide immediately</SelectItem>
                    <SelectItem value="within_week">Within 1 week</SelectItem>
                    <SelectItem value="within_month">Within 1 month</SelectItem>
                    <SelectItem value="within_quarter">Within 3 months</SelectItem>
                    <SelectItem value="annual_meeting">Annual meeting required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="urgencyReasons">What's driving your timeline?</Label>
              <Textarea
                id="urgencyReasons"
                value={data.implementationReadiness?.urgencyReasons || ''}
                onChange={(e) => updateImplementationReadiness('urgencyReasons', e.target.value)}
                placeholder="e.g., Current system failing, upcoming board elections, new regulations, resident complaints..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Stakeholder Buy-in & Resources */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Readiness Assessment
            </CardTitle>
            <CardDescription>Current state of support and resources for change</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="stakeholderBuyIn">Overall Stakeholder Buy-in</Label>
                <Select
                  value={data.implementationReadiness?.stakeholderBuyIn || ''}
                  onValueChange={(value) =>
                    updateImplementationReadiness('stakeholderBuyIn', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select buy-in level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strong">Strong - Everyone is excited</SelectItem>
                    <SelectItem value="moderate">Moderate - Most are supportive</SelectItem>
                    <SelectItem value="mixed">Mixed - Some supporters, some skeptics</SelectItem>
                    <SelectItem value="weak">Weak - Significant resistance expected</SelectItem>
                    <SelectItem value="unknown">Unknown - Haven't discussed yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="resourceAvailability">Resource Availability</Label>
                <Select
                  value={data.implementationReadiness?.resourceAvailability || ''}
                  onValueChange={(value) =>
                    updateImplementationReadiness('resourceAvailability', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fully_available">
                      Fully available - Dedicated time & budget
                    </SelectItem>
                    <SelectItem value="mostly_available">
                      Mostly available - Some time constraints
                    </SelectItem>
                    <SelectItem value="somewhat_available">
                      Somewhat available - Limited resources
                    </SelectItem>
                    <SelectItem value="limited">Limited - Major resource constraints</SelectItem>
                    <SelectItem value="needs_planning">
                      Needs planning - Must allocate resources
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="keyChampion">Key Implementation Champion</Label>
                <Input
                  id="keyChampion"
                  value={data.implementationReadiness?.keyChampion || ''}
                  onChange={(e) => updateImplementationReadiness('keyChampion', e.target.value)}
                  placeholder="Who will lead the implementation?"
                />
              </div>

              <div>
                <Label htmlFor="estimatedHoursPerWeek">Estimated Hours per Week Available</Label>
                <Select
                  value={data.implementationReadiness?.estimatedHoursPerWeek || ''}
                  onValueChange={(value) =>
                    updateImplementationReadiness('estimatedHoursPerWeek', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select time commitment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-2">1-2 hours per week</SelectItem>
                    <SelectItem value="3-5">3-5 hours per week</SelectItem>
                    <SelectItem value="6-10">6-10 hours per week</SelectItem>
                    <SelectItem value="10+">10+ hours per week</SelectItem>
                    <SelectItem value="varies">Varies by project phase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Change Management Support
            </CardTitle>
            <CardDescription>
              What support do you need to ensure successful adoption?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-base font-medium mb-3 block">Change Management Needs:</Label>
              <div className="grid md:grid-cols-2 gap-3">
                {changeManagementOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`change-${option}`}
                      checked={
                        data.implementationReadiness?.changeManagementNeeds?.includes(option) ||
                        false
                      }
                      onChange={(e) => updateChangeReadiness(option, e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor={`change-${option}`} className="text-sm">
                      {option}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="previousChangeExperience">
                Previous Technology Change Experience
              </Label>
              <Textarea
                id="previousChangeExperience"
                value={data.implementationReadiness?.previousChangeExperience || ''}
                onChange={(e) =>
                  updateImplementationReadiness('previousChangeExperience', e.target.value)
                }
                placeholder="Tell us about previous technology implementations - what worked well and what challenges did you face?"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Training Needs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Training & Support Needs</CardTitle>
            <CardDescription>What areas would benefit from focused training?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-base font-medium mb-3 block">
                Training Areas of Interest:
              </Label>
              <div className="grid md:grid-cols-2 gap-3">
                {trainingNeedOptions.map((need) => (
                  <div key={need} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`training-${need}`}
                      checked={data.implementationReadiness?.trainingNeeds?.includes(need) || false}
                      onChange={(e) => updateTrainingNeeds(need, e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor={`training-${need}`} className="text-sm">
                      {need}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferredTrainingFormat">Preferred Training Format</Label>
                <Select
                  value={data.implementationReadiness?.preferredTrainingFormat || ''}
                  onValueChange={(value) =>
                    updateImplementationReadiness('preferredTrainingFormat', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="live_demo">Live demonstration</SelectItem>
                    <SelectItem value="hands_on">Hands-on training session</SelectItem>
                    <SelectItem value="video_tutorials">Video tutorials</SelectItem>
                    <SelectItem value="written_guides">Written guides & documentation</SelectItem>
                    <SelectItem value="one_on_one">One-on-one coaching</SelectItem>
                    <SelectItem value="combination">Combination of methods</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ongoingSupportNeeds">Ongoing Support Preference</Label>
                <Select
                  value={data.implementationReadiness?.ongoingSupportNeeds || ''}
                  onValueChange={(value) =>
                    updateImplementationReadiness('ongoingSupportNeeds', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select support level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal - Just emergency support</SelectItem>
                    <SelectItem value="basic">Basic - Email support</SelectItem>
                    <SelectItem value="standard">Standard - Email + phone support</SelectItem>
                    <SelectItem value="premium">Premium - Dedicated account manager</SelectItem>
                    <SelectItem value="full_service">
                      Full service - Ongoing consultation
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Implementation Concerns */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Implementation Concerns</CardTitle>
            <CardDescription>
              What are your biggest concerns about implementing a new system?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="biggestConcerns">Primary Concerns</Label>
              <Textarea
                id="biggestConcerns"
                value={data.implementationReadiness?.biggestConcerns || ''}
                onChange={(e) => updateImplementationReadiness('biggestConcerns', e.target.value)}
                placeholder="e.g., User adoption, data migration, cost overruns, disruption to operations..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="successCriteria">
                What would make this implementation a success?
              </Label>
              <Textarea
                id="successCriteria"
                value={data.implementationReadiness?.successCriteria || ''}
                onChange={(e) => updateImplementationReadiness('successCriteria', e.target.value)}
                placeholder="e.g., 80% resident adoption, reduced administrative time, improved communication..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Readiness Score */}
        {readinessScore > 0 && (
          <Card className="bg-linear-to-r from-green-50 to-blue-50 border-green-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  {readinessScore >= 6 ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : readinessScore >= 4 ? (
                    <Clock className="w-6 h-6 text-yellow-600" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-orange-600" />
                  )}
                  <h4 className="text-lg font-semibold">
                    Implementation Readiness: {readinessScore}/8
                  </h4>
                </div>

                <div className="mb-4">
                  {getTimelineBadge(data.implementationReadiness?.timeline || '')}
                </div>

                <p className="text-sm text-gray-700">
                  {readinessScore >= 6 &&
                    'Great! Your community appears well-prepared for implementation.'}
                  {readinessScore >= 4 &&
                    readinessScore < 6 &&
                    'Good foundation. Some planning will help ensure success.'}
                  {readinessScore < 4 &&
                    "We'll work together to address readiness factors before implementation."}
                </p>

                {(tier === 'gold' || tier === 'custom') && (
                  <div className="mt-3 text-xs text-blue-700 bg-blue-100 rounded-lg p-2">
                    Your tier includes dedicated implementation support and change management
                    consulting
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-700">
          <strong>Implementation planning helps us:</strong>
        </p>
        <ul className="text-sm text-green-700 mt-2 space-y-1">
          <li>• Design a timeline that fits your community's schedule</li>
          <li>• Identify potential challenges before they become problems</li>
          <li>• Plan training that addresses your specific needs</li>
          <li>• Ensure adequate support during transition</li>
          <li>• Maximize user adoption and long-term success</li>
        </ul>
      </div>
    </div>
  );
};
